import { compare, createTaskQueue, noop, sleep, type Task, withTimeout } from '@amazon/vinyl-util'

export enum IdlePriority {
    LOW,
    MEDIUM,
    HIGH,
}

/**
 * The number of simultaneous idle tasks to execute.
 */
const SIMULTANEOUS_IDLE_TASKS = 5

/**
 * The number of seconds an idle task may take before it times out.
 */
const IDLE_TASK_TIMEOUT = 60

/**
 * Seconds after bootstrap before idle tasks may begin running.
 */
const STARTUP_IDLE_DELAY = 5

const idleQueue = createTaskQueue(compare, IdlePriority.MEDIUM)

/**
 * Enqueues a task to be done when the application is idle.
 * @param task
 * @param priority
 */
export function doWhenIdle<T>(
    task: Task<T>,
    priority: IdlePriority = IdlePriority.MEDIUM
): Promise<T> {
    return idleQueue.enqueue(
        (abort) => withTimeout(Promise.resolve(task(abort)), IDLE_TASK_TIMEOUT),
        priority
    )
}

let blocking = 0

/**
 * Provides a promise that will prevent new idle tasks from executing until the promise settles.
 */
export function blockIdleQueue<T extends Promise<any>>(work: T): T {
    blocking++
    idleQueue.simultaneous = 0
    work.finally(() => {
        if (!--blocking) {
            idleQueue.simultaneous = SIMULTANEOUS_IDLE_TASKS
        }
    }).catch(noop)
    return work
}

// Block from creation so bootstrap enqueues can't outrun the startup wait.
blockIdleQueue(sleep(STARTUP_IDLE_DELAY)).catch(noop)
